import { type FC } from "react";

import { formatAbbrevNumber } from "../../../utils/currency";
import { ActorLevelChip } from "../ActorLevelChip";
import {
  type BlackboardInstance,
  CACHE_HIT_TEXT_CLASS,
  RUN_COUNT_WARNING_TEXT_CLASS,
  getInstanceDisplayState,
  getInstanceDisplayStyle,
} from "../blackboardModels";
import { InstanceActionMenu } from "../InstanceActionMenu";
import { InstanceAvatar } from "../InstanceAvatar";
import {
  getInstanceActorWakeCacheHitPct,
  getInstanceLifetimeTokenTotal,
  getInstanceRestartWarningCount,
} from "../instanceCardMetrics";
import { ModelChip } from "../ModelChip";

interface MemberSummaryRowProps {
  member: BlackboardInstance;
  killing: boolean;
  onInspectActor: (instanceId: string) => void;
  onKill: (instanceId: string) => void;
  onLifecycleChange?: () => void;
  /** "row" = full-width roster entry; "rail" = compact two-line presence-rail
   *  entry (docked beside the Timeline, ~240px wide). */
  variant?: "row" | "rail";
}

export const MemberSummaryRow: FC<MemberSummaryRowProps> = ({
  member,
  killing,
  onInspectActor,
  onKill,
  onLifecycleChange,
  variant = "row",
}) => {
  const displayState = getInstanceDisplayState(member);
  const displayStyle = getInstanceDisplayStyle(displayState);
  const cacheHitPct = getInstanceActorWakeCacheHitPct(member);
  const totalTokens = getInstanceLifetimeTokenTotal(member);
  const restartWarningCount = getInstanceRestartWarningCount(member);

  if (variant === "rail") {
    return (
      <div className="group flex items-start gap-2 px-1.5 py-1.5 transition-colors hover:bg-slate-100/70 dark:hover:bg-slate-800/40">
        <button
          type="button"
          onClick={() => onInspectActor(member.id)}
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
          title="Inspect actor"
        >
          <InstanceAvatar
            seed={member.avatarSeed}
            size="sm"
            status={member.status}
          />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-xs font-semibold text-slate-800 dark:text-slate-100">
                {member.displayName}
              </span>
              {displayStyle.pulseClass !== null && (
                <span
                  aria-hidden
                  className={`h-1.5 w-1.5 shrink-0 ${displayStyle.pulseClass}`}
                />
              )}
            </span>
            <span className="mt-0.5 flex min-w-0 items-center gap-1.5">
              <span
                className={`shrink-0 font-mono text-[9px] uppercase tracking-wider ${displayStyle.textClass}`}
                title={displayStyle.tooltip ?? undefined}
              >
                {displayState}
              </span>
              {member.modelRef && <ModelChip modelRef={member.modelRef} />}
              {restartWarningCount !== null && (
                <span
                  className={`shrink-0 font-mono text-[9px] tabular-nums ${RUN_COUNT_WARNING_TEXT_CLASS}`}
                  title={`${restartWarningCount} restart${restartWarningCount !== 1 ? "s" : ""}`}
                >
                  ⚠ {restartWarningCount}r
                </span>
              )}
            </span>
          </span>
        </button>
        <span className="opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <InstanceActionMenu
            instanceId={member.id}
            status={member.status}
            killing={killing}
            onKill={() => onKill(member.id)}
            onLifecycleChange={onLifecycleChange}
          />
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 border border-slate-200 bg-white px-2 py-2 text-left dark:border-slate-800 dark:bg-slate-950/40">
      <button
        type="button"
        onClick={() => onInspectActor(member.id)}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
        title="Inspect actor"
      >
        <InstanceAvatar
          seed={member.avatarSeed}
          size="sm"
          status={member.status}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-semibold text-slate-800 dark:text-slate-100">
            {member.displayName}
          </span>
          <span className="mt-0.5 flex items-center gap-1.5">
            {displayStyle.pulseClass !== null && (
              <span
                aria-hidden
                className={`h-1.5 w-1.5 ${displayStyle.pulseClass}`}
              />
            )}
            <span
              className={`font-mono text-[10px] uppercase tracking-wider ${displayStyle.textClass}`}
              title={displayStyle.tooltip ?? undefined}
            >
              {displayState}
            </span>
          </span>
        </span>
      </button>
      <div className="hidden min-w-0 flex-wrap items-center justify-end gap-1.5 sm:flex">
        <ActorLevelChip level={member.evolution} />
        {member.modelRef && <ModelChip modelRef={member.modelRef} />}
        {restartWarningCount !== null && (
          <span
            className={`font-mono text-[10px] tabular-nums ${RUN_COUNT_WARNING_TEXT_CLASS}`}
            title={`${restartWarningCount} restart${restartWarningCount !== 1 ? "s" : ""}`}
          >
            ⚠ {restartWarningCount}r
          </span>
        )}
        {cacheHitPct !== null && (
          <span
            className={`font-mono text-[10px] tabular-nums ${CACHE_HIT_TEXT_CLASS}`}
            title="Actor-wake lifetime cache hit"
          >
            {cacheHitPct}% cache
          </span>
        )}
        <span
          className="font-mono text-[10px] tabular-nums text-slate-400 dark:text-slate-500"
          title={`Lifetime tokens · input + output · ${totalTokens.toLocaleString()}`}
        >
          {formatAbbrevNumber(totalTokens, "locale")} lifetime
        </span>
      </div>
      <InstanceActionMenu
        instanceId={member.id}
        status={member.status}
        killing={killing}
        onKill={() => onKill(member.id)}
        onLifecycleChange={onLifecycleChange}
      />
    </div>
  );
};
